import type {RefObject} from 'react';
import {useRef} from 'react';
import type {Placement} from '@react-types/overlays';
import {useFocusWithin} from '@sail/react-aria';
import * as React from 'react';
import type {SerializedStyles} from '@sail/engine/deprecated';
import {createView} from '@sail/engine/deprecated';
import * as Theme from '@sail/theme-classic';
import type {LayerContextType} from '@sail/global-state';
import {view} from '../../view';
import {useInteractOutside} from '../../../components/layers/vendor/useInteractOutside';
import {Layer, useCurrentLayer} from './Layer';
import {useLayerPosition} from '../../../components/layers/useLayerPosition';
import {css} from '../../css';

export type PopoverProps = {
  /** A ref object pointing to the target element of the popover */
  targetRef: RefObject<HTMLElement>;
  /** How the popover should be placed relative to the target element */
  placement?: Placement;
  /** Whether the overlay should close when focus moves outside it. */
  shouldCloseOnBlur?: boolean;
  /** Whether or not the popover should change its placement whenever there's not enough room for it to be placed according to the `placement` prop */
  shouldFlip?: boolean;
  /** @ignore */
  shouldUpdatePosition?: boolean;
  /** The contents of the popover */
  children: JSX.Element;
  /** The function to be called when the user dismisses the popover */
  onClose: () => void;
};

const LayerRef = React.forwardRef<{layer?: LayerContextType}>((_, ref) => {
  const layer = useCurrentLayer();

  React.useImperativeHandle(ref, () => ({layer: layer || undefined}));

  return null;
});

export const Popover = createView<PopoverProps>(function Popover({
  targetRef,
  placement,
  shouldCloseOnBlur,
  shouldFlip,
  shouldUpdatePosition,
  children,
  onClose,
}) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const {props, state, container} = useLayerPosition({
    placement,
    shouldFlip,
    shouldUpdatePosition,
    targetRef,
    overlayRef,
    onClose,
  });
  const layerRef = useRef<{layer?: LayerContextType}>({});

  const handleClose = (event: React.SyntheticEvent) => {
    const layerCount = layerRef.current?.layer?.layerCount;
    const isTopmostLayer = layerCount === 0;

    const elementReceivingFocus =
      event.type === 'blur'
        ? (event as React.FocusEvent).relatedTarget
        : event.target;

    if (
      isTopmostLayer &&
      // don't close if the focus is moving to the trigger element
      targetRef.current !== elementReceivingFocus &&
      // don't close if the focus is moving to a child of the trigger element
      elementReceivingFocus &&
      !targetRef.current?.contains(elementReceivingFocus as Node) &&
      // don't close if the focus is moving to the popover
      overlayRef.current !== elementReceivingFocus &&
      // don't close if focus remains within the popover
      !overlayRef.current?.contains(elementReceivingFocus as Node)
    ) {
      onClose();
    }
  };

  useInteractOutside({
    ref: overlayRef,
    onInteractOutside: handleClose,
    // If shouldCloseOnBlur is undefined, we want the default behavior to be closing on clicking away
    // for backwards compatibility.
    isDisabled: shouldCloseOnBlur === false,
  });

  const {focusWithinProps} = useFocusWithin({
    isDisabled: !shouldCloseOnBlur,
    onBlurWithin: handleClose,
  });

  // Handle the escape key
  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  }

  const getMarginStyles = (): SerializedStyles => {
    const statePlacement = state?.position?.placement;
    switch (statePlacement) {
      case 'top':
        return css({marginBottom: 'xsmall'});
      case 'bottom':
        return css({marginTop: 'xsmall'});
      case 'left':
        return css({marginRight: 'xsmall'});
      case 'right':
        return css({marginLeft: 'xsmall'});
      default:
        return css({margin: 'xsmall'});
    }
  };

  return (
    <Layer
      type="partial"
      container={container}
      uses={[props, focusWithinProps, {onKeyDown}, {ref: overlayRef}]}
    >
      <LayerRef ref={layerRef} />
      <view.div uses={[Theme.Popover, getMarginStyles()]}>{children}</view.div>
    </Layer>
  );
});
